import { Loader } from '@mantine/core';
import type { ActorDto } from '@newo/api-client';
import { motion, useScroll, useTransform } from 'framer-motion';
import { type FC, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHostContext } from '@/context/HostContext.tsx';
import useQueryParams from '@/hooks/useQueryParams';
import useTraining from '@/hooks/useTraining';
import AgentIntroContent from '@/pages/AgentIntro/components/AgentIntroContent';
import HowDoItWorkVideo from '@/pages/AgentIntro/components/HowDoItWorkVideo';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app';
import { cn, normalizeValue } from '@/utils';
import TrainingSourceSelector from './components/TrainingSourceSelector';

type AgentIntroProps = {
	actor?: ActorDto;
};

const AgentIntro: FC<AgentIntroProps> = ({ actor }) => {
	const [_, setSearchParams] = useSearchParams();
	const setAgentQuery = useAppStore((s) => s.setAgentQuery);
	const { isOverlayMode } = useHostContext();
	const { startTraining, isPending } = useTraining(actor);
	const { isMiniCreatorMode } = useHostContext();
	const { localization } = useHostContext();

	const {
		params: { google_map_url, source: websiteUrl, title, subtitle, buttonLabel }
	} = useQueryParams();

	const containerRef = useRef<HTMLDivElement | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: need to run only one time
	useEffect(() => {
		setAgentQuery({
			title: normalizeValue(title),
			subtitle: normalizeValue(subtitle),
			buttonLabel: normalizeValue(buttonLabel)
		});

		const nextParams = new URLSearchParams(location.search);
		nextParams.delete('title');
		nextParams.delete('subtitle');
		nextParams.delete('buttonLabel');

		setSearchParams(nextParams, { replace: true });
	}, []);

	const { scrollY } = useScroll({
		target: isOverlayMode ? containerRef : undefined
	});

	const progress = useTransform(scrollY, (y) => {
		const height = isOverlayMode
			? (containerRef.current?.clientHeight ?? window.innerHeight)
			: window.innerHeight;

		return Math.min(Math.max(y / (height * 0.25), 0), 1);
	});

	const peekPx = useTransform(
		progress,
		[0, 1],
		[
			() =>
				(isOverlayMode
					? (containerRef.current?.clientHeight ?? window.innerHeight)
					: window.innerHeight) * 0.15,
			0
		]
	);

	const translateY = useTransform(peekPx, (v) => v);

	if (google_map_url || websiteUrl) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader />
			</div>
		);
	}

	return (
		<div
			className={`w-full flex flex-col gap-5 items-center ${
				isOverlayMode || isMiniCreatorMode ? 'h-full overflow-y-auto' : ''
			}`}
			ref={isOverlayMode ? containerRef : undefined}
		>
			<div
				className={cn(
					'p-6 sm:p-16 bg-white flex flex-col max-w-4xl w-full gap-[32px] sm:gap-[53px] items-center text-center ' +
						'h-full rounded-[40px] shadow-xl',
					{ '!p-4 rounded-[20px] !gap-8': isMiniCreatorMode }
				)}
			>
				<AgentIntroContent />

				<TrainingSourceSelector
					handleSubmit={startTraining}
					isPending={isPending}
				/>

				<div className="text-balance text-gray-700 text-sm font-medium">
					{localization.translate('creator', 'trainingSourceSelection.hint')}
				</div>
			</div>

			{!partnerMetadata.isVideosHidden() && (
				<motion.div
					className={cn(
						'p-6 sm:p-16 w-full flex flex-col max-w-4xl gap-[32px] sm:gap-[53px] items-center text-center',
						{ '!gap-8 !p-4': isMiniCreatorMode }
					)}
					style={{ y: translateY }}
				>
					<HowDoItWorkVideo />
				</motion.div>
			)}
		</div>
	);
};

export default AgentIntro;
